import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { CustomerService } from '../../services/customer.service';
import type { CustomerDTO, MitConfigDTO, MitMandateDTO } from '../../core/models';
import { MitService, mitApiError } from '../../services/mit.service';
import { MitLimitsFieldsComponent, buildMitLimitsForm, mitLimitsFromForm } from './mit-limits-fields.component';
import {
  MitChargeLinesFieldsComponent,
  buildMitChargeLinesForm,
  mitChargeLinesFromForm,
  mitChargeLinesTotal
} from './mit-charge-lines-fields.component';
import {
  MitComercialMetadataFieldsComponent,
  buildMitComercialMetadataForm,
  mitComercialMetadataFromForm
} from './mit-comercial-metadata-fields.component';

type LabeledCustomer = CustomerDTO & { displayLabel: string };

function validityOrder(group: AbstractControl): ValidationErrors | null {
  const from = group.get('validFrom')?.value as Date | null;
  const until = group.get('validUntil')?.value as Date | null;
  return from && until && until.getTime() <= from.getTime() ? { validityOrder: true } : null;
}

@Component({
  selector: 'app-mit-mandate-form',
  templateUrl: './mit-mandate-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    ButtonModule,
    FloatLabelModule,
    InputTextModule,
    AutoCompleteModule,
    DatePickerModule,
    DialogModule,
    MitLimitsFieldsComponent,
    MitChargeLinesFieldsComponent,
    MitComercialMetadataFieldsComponent
  ]
})
export class MitMandateFormComponent {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private toastr = inject(ToastrService);
  private mitService = inject(MitService);
  private customerService = inject(CustomerService);
  private customerQuery = new Subject<string>();

  config = signal<MitConfigDTO | null>(null);
  configLoaded = signal(false);
  customerOptions = signal<LabeledCustomer[]>([]);
  selectedCustomer = signal<CustomerDTO | null>(null);
  customerLoading = signal(false);
  saving = signal(false);
  apiError = signal<string | null>(null);
  created = signal<MitMandateDTO | null>(null);

  mitEnabled = computed(() => this.config()?.enabled === true);
  customerWithoutEmail = computed(() => {
    const customer = this.selectedCustomer();
    return !!customer && !customer.email?.trim();
  });

  readonly today = new Date();

  limitsForm = buildMitLimitsForm(this.fb);
  initialChargeLines = buildMitChargeLinesForm(this.fb);
  comercialMetadata = buildMitComercialMetadataForm(this.fb);
  /** Sin primer cobro, el registro hace un cargo de validación que se cancela de inmediato. */
  withInitialCharge = signal(false);
  form = this.fb.group({
    customer: this.fb.control<LabeledCustomer | string | null>(null, Validators.required),
    purpose: ['', [Validators.required, Validators.maxLength(250)]],
    initialChargeConcept: ['', Validators.maxLength(250)],
    initialChargeLines: this.initialChargeLines,
    externalReference: ['', Validators.maxLength(100)],
    validFrom: this.fb.control<Date | null>(null),
    validUntil: this.fb.control<Date | null>(null),
    limits: this.limitsForm,
    comercialMetadata: this.comercialMetadata
  }, { validators: [validityOrder, (group) => this.initialWithinLimit(group), (group) => this.withinCompanyCaps(group)] });

  constructor() {
    this.customerQuery.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed()).subscribe((query) => {
      this.customerService.listCustomers({
        'legalName[lk]': query,
        'comercialName[lk]': query,
        'taxId[lk]': query
      }, { page: 0, size: 20 }).subscribe((response) => {
        this.customerOptions.set((response.body ?? []).map((customer) => this.labeled(customer)));
      });
    });
    this.toggleInitialCharge(false);
    this.loadConfig();
    const customerId = Number(this.route.snapshot.queryParamMap.get('customerId'));
    if (customerId) {
      this.loadCustomer(customerId);
    }
  }

  onCustomerSearch(event: { query: string }): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.customerOptions.set([]);
      return;
    }
    this.customerQuery.next(query);
  }

  /** Las líneas solo se validan cuando el mandato lleva primer cobro. */
  toggleInitialCharge(enabled: boolean): void {
    this.withInitialCharge.set(enabled);
    if (enabled) {
      this.initialChargeLines.enable();
      this.form.controls.initialChargeConcept.enable();
    } else {
      this.initialChargeLines.disable();
      this.form.controls.initialChargeConcept.disable();
    }
    this.form.updateValueAndValidity();
  }

  /** El listado no trae el correo; se consulta el cliente para validar que pueda recibir la liga. */
  onCustomerSelect(customer: LabeledCustomer): void {
    if (customer?.id != null) this.loadCustomer(customer.id);
  }

  onCustomerClear(): void {
    this.selectedCustomer.set(null);
  }

  save(): void {
    this.form.markAllAsTouched();
    this.apiError.set(null);
    const customer = this.selectedCustomer();
    if (this.form.invalid || !customer?.id || this.customerWithoutEmail() || !this.mitEnabled() || this.saving()) {
      return;
    }
    const raw = this.form.getRawValue();
    this.saving.set(true);
    this.mitService.create({
      customerId: customer.id,
      purpose: raw.purpose!.trim(),
      initialCharge: this.withInitialCharge()
        ? { concept: raw.initialChargeConcept?.trim() || undefined, lines: mitChargeLinesFromForm(this.initialChargeLines) }
        : undefined,
      externalReference: raw.externalReference?.trim() || undefined,
      validFrom: raw.validFrom ? raw.validFrom.toISOString() : undefined,
      validUntil: raw.validUntil ? raw.validUntil.toISOString() : undefined,
      limits: mitLimitsFromForm(this.limitsForm),
      comercialMetadata: mitComercialMetadataFromForm(this.comercialMetadata)
    }).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.created.set(response.body ?? null);
      },
      error: (err) => {
        this.saving.set(false);
        this.apiError.set(mitApiError(err, 'No se pudo crear el mandato').message);
      }
    });
  }


  goToDetail(): void {
    const mandate = this.created();
    if (mandate?.id) void this.router.navigate(['/mit-mandates', mandate.id], { replaceUrl: true });
  }

  invalid(name: string): boolean {
    const control = this.form.get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  private initialWithinLimit(group: AbstractControl): ValidationErrors | null {
    if (!this.withInitialCharge?.()) return null;
    const initial = this.initialChargeLines ? mitChargeLinesTotal(this.initialChargeLines) : null;
    const perTx = group.get('limits.maxAmountPerTransaction')?.value as number | null;
    return initial != null && perTx != null && initial > perTx ? { initialOverLimit: true } : null;
  }

  private loadConfig(): void {
    const companyId = this.auth.getCompanyId();
    if (companyId == null) {
      this.configLoaded.set(true);
      return;
    }
    this.mitService.getConfig(companyId).subscribe({
      next: (response) => {
        this.config.set(response.body ?? null);
        this.applyCompanyDefaults(response.body ?? null);
        this.configLoaded.set(true);
      },
      error: () => this.configLoaded.set(true)
    });
  }

  /**
   * Precarga los topes de la compañía como límites del mandato y, si la compañía limita la vigencia, la vigencia
   * máxima (la API la exige en ese caso). Solo llena campos que el usuario no ha tocado.
   */
  private applyCompanyDefaults(config: MitConfigDTO | null): void {
    if (!config) return;
    const perMonth = config.maxAmountPerMonthPerMandateCap ?? null;
    const perTx = config.maxAmountPerTransactionCap != null && perMonth != null
      ? Math.min(config.maxAmountPerTransactionCap, perMonth)
      : config.maxAmountPerTransactionCap ?? null;
    this.prefill(this.limitsForm.get('maxAmountPerTransaction'), perTx);
    this.prefill(this.limitsForm.get('maxAmountPerMonth'), perMonth);
    if (config.maxMandateValidityDays) {
      const until = new Date();
      until.setDate(until.getDate() + config.maxMandateValidityDays);
      this.prefill(this.form.controls.validUntil, until);
    }
    this.form.updateValueAndValidity();
  }

  private prefill<T>(control: AbstractControl | null, value: T | null): void {
    if (control && value != null && control.value == null && control.pristine) control.setValue(value);
  }

  /** Mismos topes que valida la API al crear el mandato. */
  private withinCompanyCaps(group: AbstractControl): ValidationErrors | null {
    const config = this.config?.();
    if (!config) return null;
    const errors: ValidationErrors = {};
    const perTx = group.get('limits.maxAmountPerTransaction')?.value as number | null;
    const perMonth = group.get('limits.maxAmountPerMonth')?.value as number | null;
    if (perTx != null && config.maxAmountPerTransactionCap != null && perTx > config.maxAmountPerTransactionCap) {
      errors['perTxOverCap'] = true;
    }
    if (perMonth != null && config.maxAmountPerMonthPerMandateCap != null && perMonth > config.maxAmountPerMonthPerMandateCap) {
      errors['monthOverCap'] = true;
    }
    if (config.maxMandateValidityDays) {
      const from = (group.get('validFrom')?.value as Date | null) ?? new Date();
      const until = group.get('validUntil')?.value as Date | null;
      const max = new Date(from.getTime() + config.maxMandateValidityDays * 86_400_000);
      if (!until) errors['validityRequired'] = true;
      else if (until.getTime() > max.getTime()) errors['validityOverCap'] = true;
    }
    return Object.keys(errors).length ? errors : null;
  }

  private loadCustomer(id: number): void {
    this.customerLoading.set(true);
    this.customerService.getCustomerById(id).subscribe({
      next: (response) => {
        this.customerLoading.set(false);
        const customer = response.body;
        if (!customer) return;
        this.selectedCustomer.set(customer);
        this.form.controls.customer.setValue(this.labeled(customer), { emitEvent: false });
      },
      error: () => {
        this.customerLoading.set(false);
        this.toastr.error('No se pudo cargar el cliente');
      }
    });
  }

  private labeled(customer: CustomerDTO): LabeledCustomer {
    return { ...customer, displayLabel: customer.comercialName || customer.legalName || customer.taxId || '' };
  }
}
